import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUseCases, useCreateUseCase, useUpdateUseCase, useDeleteUseCase } from '../hooks/useUseCases';
import { useSources } from '../hooks/useSource';
import { useFeatureFlag } from '../services/featureFlag.service';
import { FeatureFlag } from '../services/featureFlag.service';
import type { UseCase, UseCaseDataSource, UseCaseStatus } from '../services/useCase.service';
import type { Source } from '../services/source.service';
import ApiService from '../services/api.service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Database, CheckCircle2, Clock, Search, X, Play, MessageSquare, BookOpen, Check, XCircle } from 'lucide-react';
import { Ellie } from '../components/Ellie';
import { FullScreenTakeover } from '../components/FullScreenTakeover';
import { shortid } from '../services/id.service';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { SidebarTrigger } from '../components/ui/sidebar';

const feedbackMarkdownComponents = {
  h1: ({ className, ...props }: any) => (
    <h1 className={cn('text-2xl font-bold mb-4 first:mt-0 mt-6', className)} {...props} />
  ),
  h2: ({ className, ...props }: any) => (
    <h2 className={cn('text-xl font-bold mb-3 first:mt-0 mt-5', className)} {...props} />
  ),
  h3: ({ className, ...props }: any) => (
    <h3 className={cn('text-lg font-semibold mb-2 first:mt-0 mt-4', className)} {...props} />
  ),
  h4: ({ className, ...props }: any) => (
    <h4 className={cn('text-base font-semibold mb-2 first:mt-0 mt-3', className)} {...props} />
  ),
  p: ({ className, ...props }: any) => (
    <p className={cn('mb-4 last:mb-0', className)} {...props} />
  ),
  a: ({ className, ...props }: any) => (
    <a className={cn('text-primary underline hover:text-primary/80', className)} {...props} />
  ),
  blockquote: ({ className, ...props }: any) => (
    <blockquote className={cn('border-l-4 border-border pl-4 italic my-4', className)} {...props} />
  ),
  ul: ({ className, ...props }: any) => (
    <ul className={cn('list-disc mb-4 space-y-1 pl-6', className)} {...props} />
  ),
  ol: ({ className, ...props }: any) => (
    <ol className={cn('list-decimal mb-4 space-y-1 pl-6', className)} {...props} />
  ),
  li: ({ className, ...props }: any) => (
    <li className={cn('pl-2', className)} {...props} />
  ),
  hr: ({ className, ...props }: any) => (
    <hr className={cn('my-4 border-border', className)} {...props} />
  ),
  table: ({ className, ...props }: any) => (
    <table className={cn('w-full border-collapse border border-border my-4', className)} {...props} />
  ),
  th: ({ className, ...props }: any) => (
    <th className={cn('border border-border px-4 py-2 bg-muted font-semibold text-left', className)} {...props} />
  ),
  td: ({ className, ...props }: any) => (
    <td className={cn('border border-border px-4 py-2', className)} {...props} />
  ),
  code: ({ className, children, ...props }: any) => (
    <code className={cn('bg-muted px-1.5 py-0.5 rounded text-sm font-mono', className)} {...props}>
      {children}
    </code>
  ),
  pre: ({ children, ...props }: any) => (
    <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4 [&>code]:block [&>code]:p-0 [&>code]:bg-transparent" {...props}>
      {children}
    </pre>
  ),
};

function useKBArticle(articleId?: string | null) {
  return useQuery({
    queryKey: ['kbArticle', articleId],
    queryFn: () =>
      ApiService.getInstance().request(
        'GET',
        `/knowledge-base-items/${articleId}`
      ) as Promise<{ record: { id: string; name: string; body?: string; tags: string[]; created_at: string } }>,
    enabled: !!articleId,
    staleTime: 30 * 1000,
    select: (data) => data.record,
  });
}

function ValidationFeedbackDialog({
  open,
  onOpenChange,
  articleId,
  sourceName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  articleId: string;
  sourceName: string;
}) {
  const { data: article, isLoading } = useKBArticle(open ? articleId : null);

  return (
    <FullScreenTakeover
      show={open}
      onHide={() => onOpenChange(false)}
      title={`Validation Feedback: ${sourceName}`}
    >
      <div className="flex-1 min-h-0 overflow-y-auto py-12 px-24">
        {article?.created_at && (
          <p className="text-sm text-muted-foreground mb-4">
            {new Date(article.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
        )}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : article?.body ? (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <Markdown remarkPlugins={[remarkGfm]} components={feedbackMarkdownComponents}>{article.body}</Markdown>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No feedback content available.</p>
        )}
      </div>
    </FullScreenTakeover>
  );
}

const STATUS_CONFIG: Record<UseCaseStatus, { label: string; variant: 'warning' | 'info' | 'success' | 'destructive' }> = {
  PENDING: { label: 'Pending', variant: 'warning' },
  CONNECTED: { label: 'Connected', variant: 'info' },
  VALIDATING: { label: 'Validating', variant: 'info' },
  CHANGES_REQUESTED: { label: 'Changes Requested', variant: 'destructive' },
  APPROVED: { label: 'Approved', variant: 'success' },
  PLAN_APPROVED: { label: 'Plan Approved', variant: 'success' },
  IMPLEMENTATION_IN_PROGRESS: { label: 'Implementation In Progress', variant: 'info' },
  IMPLEMENTATION_COMPLETE: { label: 'Implementation Complete', variant: 'success' },
};

function StatusBadge({ status }: { status: UseCaseStatus }) {
  const config = STATUS_CONFIG[status] || { label: status, variant: 'info' as const };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

function DataSourceRow({
  ds,
  sources,
  onStartValidation,
  onContinueValidation,
  onRestartValidation,
}: {
  ds: UseCaseDataSource;
  sources: Source[];
  onStartValidation?: () => void;
  onContinueValidation?: () => void;
  onRestartValidation?: () => void;
}) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const linkedSource = ds.source_id ? sources.find((s) => s.id === ds.source_id) : null;
  const canContinue = ds.status === 'VALIDATING' && ds.thread_id && ds.source_id;
  const hasFeedback = !!ds.validation_kb_article_id;

  return (
    <>
      <div className="flex items-center justify-between gap-2 py-2 border-b last:border-b-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Database className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{ds.name}</p>
            {linkedSource ? (
              <p className="text-xs text-muted-foreground truncate">Connected: {linkedSource.name}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Not connected</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(ds.status === 'CONNECTED' || (ds.status === 'VALIDATING' && !ds.thread_id)) && ds.source_id && onStartValidation && (
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onStartValidation}>
              <Play className="h-3 w-3 mr-1" />
              Start Validation
            </Button>
          )}
          {canContinue && onContinueValidation && (
            <div className="flex flex-col items-end gap-0.5">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onContinueValidation}>
                <Play className="h-3 w-3 mr-1" />
                Continue Validation
              </Button>
              {onRestartValidation && (
                <button
                  className="text-[10px] text-muted-foreground hover:text-foreground underline"
                  onClick={onRestartValidation}
                >
                  Restart Validation
                </button>
              )}
            </div>
          )}
          {hasFeedback && (ds.status === 'APPROVED' || ds.status === 'CHANGES_REQUESTED') && (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFeedbackOpen(true)}>
              <MessageSquare className="h-3.5 w-3.5" />
            </Button>
          )}
          <StatusBadge status={ds.status} />
        </div>
      </div>

      {hasFeedback && (
        <ValidationFeedbackDialog
          open={feedbackOpen}
          onOpenChange={setFeedbackOpen}
          articleId={ds.validation_kb_article_id!}
          sourceName={linkedSource?.name || ds.name}
        />
      )}
    </>
  );
}

function UseCaseCard({
  useCase,
  sources,
  canManage,
  onEdit,
  onDelete,
}: {
  useCase: UseCase;
  sources: Source[];
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [discoverySession, setDiscoverySession] = useState<{ threadId: string; initialQuestion?: string } | null>(null);
  const [discoveryComplete, setDiscoveryComplete] = useState(false);
  const [discoveryFeedbackOpen, setDiscoveryFeedbackOpen] = useState(false);
  const [edaSession, setEdaSession] = useState<{ sourceId: string; dataSourceId: string; sourceName: string; threadId: string; initialQuestion?: string } | null>(null);
  const [validationComplete, setValidationComplete] = useState(false);
  const [dataModelingSession, setDataModelingSession] = useState<{ threadId: string; initialQuestion?: string } | null>(null);
  const [modelingComplete, setModelingComplete] = useState(false);
  const [modelingFeedbackOpen, setModelingFeedbackOpen] = useState(false);
  const queryClient = useQueryClient();
  const updateMutation = useUpdateUseCase();
  const approvedCount = useCase.data_sources.filter((ds) => ds.status === 'APPROVED').length;
  const totalCount = useCase.data_sources.length;
  const allApproved = totalCount > 0 && approvedCount === totalCount;

  const setDataSourceStatus = (dataSourceId: string, newStatus: UseCaseStatus) => {
    const updatedSources = useCase.data_sources.map((ds) =>
      ds.id === dataSourceId ? { ...ds, status: newStatus } : ds
    );
    updateMutation.mutate({ id: useCase.id, data_sources: updatedSources });
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-lg">{useCase.name}</CardTitle>
              {useCase.description && <CardDescription>{useCase.description}</CardDescription>}
            </div>
            {canManage && (
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onDelete}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {useCase.discovery_status === 'APPROVED' ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">Use Case Discovery</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {useCase.discovery_kb_article_id && useCase.discovery_status === 'APPROVED' && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDiscoveryFeedbackOpen(true)}>
                    <MessageSquare className="h-3.5 w-3.5" />
                  </Button>
                )}
                <StatusBadge status={useCase.discovery_status} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {useCase.discovery_status === 'APPROVED'
                ? "Discovery complete — we've confirmed your business context and how each data source applies to this use case."
                : "We'll confirm what we already know about your business, then dig into how each data source is used for this use case."}
            </p>
            {useCase.discovery_status !== 'APPROVED' && !useCase.discovery_thread_id && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  updateMutation.mutate({ id: useCase.id, discovery_status: 'VALIDATING' });
                  setDiscoveryComplete(false);
                  setDiscoverySession({
                    threadId: shortid(),
                    initialQuestion: `Let's talk about the "${useCase.name}" use case. I'd love to learn more about your business and how you plan to use your data.`,
                  });
                }}
              >
                <Play className="h-3 w-3 mr-1" />
                Start discovery interview
              </Button>
            )}
            {useCase.discovery_status !== 'APPROVED' && useCase.discovery_thread_id && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setDiscoveryComplete(false);
                    setDiscoverySession({
                      threadId: useCase.discovery_thread_id!,
                    });
                  }}
                >
                  <Play className="h-3 w-3 mr-1" />
                  Continue interview
                </Button>
                <button
                  className="text-[10px] text-muted-foreground hover:text-foreground underline"
                  onClick={() => {
                    setDiscoveryComplete(false);
                    setDiscoverySession({
                      threadId: shortid(),
                      initialQuestion: `Let's talk about the "${useCase.name}" use case. I'd love to learn more about your business and how you plan to use your data.`,
                    });
                  }}
                >
                  Restart
                </button>
              </div>
            )}
          </div>

          <div className="pt-2 border-t">
            <div className="mb-2">
              <h4 className="text-sm font-medium">
                Data Sources ({approvedCount}/{totalCount} approved)
              </h4>
              <p className="text-xs text-muted-foreground mt-1">
                We'll connect to each source and load your data into Pliable. Once connected, we'll ask you to validate that the data looks correct before moving forward.
              </p>
            </div>
            {useCase.data_sources.length > 0 ? (
              <div className="space-y-0">
                {useCase.data_sources.map((ds, idx) => {
                  const linkedSource = ds.source_id ? sources.find((s) => s.id === ds.source_id) : null;
                  return (
                    <DataSourceRow
                      key={idx}
                      ds={ds}
                      sources={sources}
                      onStartValidation={() => {
                        setDataSourceStatus(ds.id, 'VALIDATING');
                        setValidationComplete(false);
                        setEdaSession({
                          sourceId: ds.source_id!,
                          dataSourceId: ds.id,
                          sourceName: linkedSource?.name || ds.name,
                          threadId: shortid(),
                          initialQuestion: `Help me validate my new ${linkedSource?.name || ds.name} data source`,
                        });
                      }}
                      onContinueValidation={() => {
                        setValidationComplete(false);
                        setEdaSession({
                          sourceId: ds.source_id!,
                          dataSourceId: ds.id,
                          sourceName: linkedSource?.name || ds.name,
                          threadId: ds.thread_id!,
                        });
                      }}
                      onRestartValidation={() => {
                        setValidationComplete(false);
                        setEdaSession({
                          sourceId: ds.source_id!,
                          dataSourceId: ds.id,
                          sourceName: linkedSource?.name || ds.name,
                          threadId: shortid(),
                          initialQuestion: `Help me validate my new ${linkedSource?.name || ds.name} data source`,
                        });
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No data sources defined</p>
            )}
          </div>

          <div className="pt-2 border-t space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {useCase.data_modeling_status === 'IMPLEMENTATION_COMPLETE' ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">Data Modeling</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {useCase.data_modeling_kb_article_id && (['PLAN_APPROVED', 'IMPLEMENTATION_IN_PROGRESS', 'IMPLEMENTATION_COMPLETE'] as UseCaseStatus[]).includes(useCase.data_modeling_status) && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setModelingFeedbackOpen(true)}>
                    <MessageSquare className="h-3.5 w-3.5" />
                  </Button>
                )}
                <StatusBadge status={useCase.data_modeling_status} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {useCase.data_modeling_status === 'PLAN_APPROVED'
                ? "Your data modeling plan has been approved. Implementation will begin shortly — you'll be notified once it's complete."
                : useCase.data_modeling_status === 'IMPLEMENTATION_IN_PROGRESS'
                ? "Implementation is in progress. You'll be alerted once it's complete."
                : useCase.data_modeling_status === 'IMPLEMENTATION_COMPLETE'
                ? "Implementation is complete. Your data model is ready."
                : allApproved
                ? "All data sources are validated. Start a data modeling interview to define KPIs, cleanup rules, and how this data integrates with your model."
                : "Once your sources are connected, we'll clean up the data and integrate it into your Pliable data model — your data won't live in a silo, it'll be interconnected with everything else in your model."}
            </p>
            {allApproved && !(['PLAN_APPROVED', 'IMPLEMENTATION_IN_PROGRESS', 'IMPLEMENTATION_COMPLETE'] as UseCaseStatus[]).includes(useCase.data_modeling_status) && !useCase.data_modeling_thread_id && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  updateMutation.mutate({ id: useCase.id, data_modeling_status: 'VALIDATING' });
                  setModelingComplete(false);
                  setDataModelingSession({
                    threadId: shortid(),
                    initialQuestion: `Let's start the data modeling interview for the "${useCase.name}" use case.`,
                  });
                }}
              >
                <Play className="h-3 w-3 mr-1" />
                Start data modeling interview
              </Button>
            )}
            {allApproved && !(['PLAN_APPROVED', 'IMPLEMENTATION_IN_PROGRESS', 'IMPLEMENTATION_COMPLETE'] as UseCaseStatus[]).includes(useCase.data_modeling_status) && useCase.data_modeling_thread_id && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setModelingComplete(false);
                    setDataModelingSession({
                      threadId: useCase.data_modeling_thread_id!,
                    });
                  }}
                >
                  <Play className="h-3 w-3 mr-1" />
                  Continue interview
                </Button>
                <button
                  className="text-[10px] text-muted-foreground hover:text-foreground underline"
                  onClick={() => {
                    setModelingComplete(false);
                    setDataModelingSession({
                      threadId: shortid(),
                      initialQuestion: `Let's start the data modeling interview for the "${useCase.name}" use case.`,
                    });
                  }}
                >
                  Restart
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!discoverySession} onOpenChange={(open) => {
        if (!open) {
          setDiscoverySession(null);
          setDiscoveryComplete(false);
          queryClient.invalidateQueries({ queryKey: ['useCases'] });
          queryClient.invalidateQueries({ queryKey: ['kbPendingArticles'] });
        }
      }}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Use Case Discovery: {useCase.name}</DialogTitle>
            <DialogDescription>
              Ellie will learn about your business, your use case, and how you use your data sources.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex-1 min-h-0 px-0 overflow-hidden">
            {discoverySession && (
              <Ellie
                agentType="use_case_discovery"
                useCaseId={useCase.id}
                conversationId={discoverySession.threadId}
                initialQuestion={discoverySession.initialQuestion}
                onValidationComplete={() => setDiscoveryComplete(true)}
              />
            )}
          </DialogBody>
          {discoveryComplete && (
            <DialogFooter>
              <Button onClick={() => {
                setDiscoverySession(null);
                setDiscoveryComplete(false);
                queryClient.invalidateQueries({ queryKey: ['useCases'] });
                queryClient.invalidateQueries({ queryKey: ['kbPendingArticles'] });
              }}>
                Close Discovery
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {useCase.discovery_kb_article_id && (
        <ValidationFeedbackDialog
          open={discoveryFeedbackOpen}
          onOpenChange={setDiscoveryFeedbackOpen}
          articleId={useCase.discovery_kb_article_id}
          sourceName={useCase.name}
        />
      )}

      <Dialog open={!!edaSession} onOpenChange={(open) => {
        if (!open) {
          setEdaSession(null);
          setValidationComplete(false);
          queryClient.invalidateQueries({ queryKey: ['useCases'] });
          queryClient.invalidateQueries({ queryKey: ['kbPendingArticles'] });
        }
      }}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Data Validation: {edaSession?.sourceName}</DialogTitle>
            <DialogDescription>
              Ellie will explore this data source, profile the tables, and create an artifact with her findings.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex-1 min-h-0 px-0 overflow-hidden">
            {edaSession && (
              <Ellie
                agentType="eda"
                sourceId={edaSession.sourceId}
                dataSourceId={edaSession.dataSourceId}
                useCaseId={useCase.id}
                conversationId={edaSession.threadId}
                initialQuestion={edaSession.initialQuestion}
                onValidationComplete={() => setValidationComplete(true)}
              />
            )}
          </DialogBody>
          {validationComplete && (
            <DialogFooter>
              <Button onClick={() => {
                setEdaSession(null);
                setValidationComplete(false);
                queryClient.invalidateQueries({ queryKey: ['useCases'] });
                queryClient.invalidateQueries({ queryKey: ['kbPendingArticles'] });
              }}>
                Close Validation
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!dataModelingSession} onOpenChange={(open) => {
        if (!open) {
          setDataModelingSession(null);
          setModelingComplete(false);
          queryClient.invalidateQueries({ queryKey: ['useCases'] });
          queryClient.invalidateQueries({ queryKey: ['kbPendingArticles'] });
        }
      }}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Data Modeling Interview: {useCase.name}</DialogTitle>
            <DialogDescription>
              Ellie will review the validation results and interview you to define KPIs, data cleanup rules, and modeling recommendations.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex-1 min-h-0 px-0 overflow-hidden">
            {dataModelingSession && (
              <Ellie
                agentType="data_modeling"
                useCaseId={useCase.id}
                conversationId={dataModelingSession.threadId}
                initialQuestion={dataModelingSession.initialQuestion}
                onValidationComplete={() => setModelingComplete(true)}
              />
            )}
          </DialogBody>
          {modelingComplete && (
            <DialogFooter>
              <Button onClick={() => {
                setDataModelingSession(null);
                setModelingComplete(false);
                queryClient.invalidateQueries({ queryKey: ['useCases'] });
                queryClient.invalidateQueries({ queryKey: ['kbPendingArticles'] });
              }}>
                Close Interview
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {useCase.data_modeling_kb_article_id && (
        <ValidationFeedbackDialog
          open={modelingFeedbackOpen}
          onOpenChange={setModelingFeedbackOpen}
          articleId={useCase.data_modeling_kb_article_id}
          sourceName={useCase.name}
        />
      )}
    </>
  );
}

function UseCaseFormDialog({
  open,
  onOpenChange,
  initialValues,
  sources,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: UseCase | null;
  sources: Source[];
  onSubmit: (data: { name: string; description: string; data_sources: UseCaseDataSource[]; discovery_status: UseCaseStatus; data_modeling_status: UseCaseStatus }) => void;
}) {
  const [name, setName] = useState(initialValues?.name || '');
  const [description, setDescription] = useState(initialValues?.description || '');
  const [dataSources, setDataSources] = useState<UseCaseDataSource[]>(
    initialValues?.data_sources || []
  );
  const [discoveryStatus, setDiscoveryStatus] = useState<UseCaseStatus>(
    initialValues?.discovery_status || 'PENDING'
  );
  const [dataModelingStatus, setDataModelingStatus] = useState<UseCaseStatus>(
    initialValues?.data_modeling_status || 'PENDING'
  );
  const [newDsName, setNewDsName] = useState('');
  const isEditing = !!initialValues;

  const handleAddDataSource = () => {
    if (!newDsName.trim()) return;
    setDataSources([...dataSources, { id: crypto.randomUUID(), name: newDsName.trim(), status: 'PENDING' }]);
    setNewDsName('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim(), data_sources: dataSources, discovery_status: discoveryStatus, data_modeling_status: dataModelingStatus });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Use Case' : 'New Use Case'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the use case details, data sources, and statuses.'
                : 'Define a new use case and its required data sources.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Customer Churn Analysis"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the use case..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Data Sources</label>
              {dataSources.map((ds, idx) => (
                <div key={idx} className="space-y-2 rounded-md border p-3">
                  <div className="flex items-center gap-2">
                    <Input
                      value={ds.name}
                      onChange={(e) => {
                        const updated = [...dataSources];
                        updated[idx] = { ...ds, name: e.target.value };
                        setDataSources(updated);
                      }}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => setDataSources(dataSources.filter((_, i) => i !== idx))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {isEditing && (
                    <div className="flex items-center gap-2">
                      <Select
                        value={ds.status}
                        onValueChange={(val) => {
                          const updated = [...dataSources];
                          updated[idx] = { ...ds, status: val as UseCaseStatus };
                          setDataSources(updated);
                        }}
                      >
                        <SelectTrigger className="h-8 w-[130px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PENDING">Pending</SelectItem>
                          <SelectItem value="CONNECTED">Connected</SelectItem>
                          <SelectItem value="VALIDATING">Validating</SelectItem>
                          <SelectItem value="CHANGES_REQUESTED">Changes Requested</SelectItem>
                          <SelectItem value="APPROVED">Approved</SelectItem>
                          <SelectItem value="PLAN_APPROVED">Plan Approved</SelectItem>
                          <SelectItem value="IMPLEMENTATION_IN_PROGRESS">Implementation In Progress</SelectItem>
                          <SelectItem value="IMPLEMENTATION_COMPLETE">Implementation Complete</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={ds.source_id || '__none__'}
                        onValueChange={(val) => {
                          const updated = [...dataSources];
                          updated[idx] = { ...ds, source_id: val === '__none__' ? null : val };
                          setDataSources(updated);
                        }}
                      >
                        <SelectTrigger className="h-8 flex-1 text-xs">
                          <SelectValue placeholder="Link source..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Not connected</SelectItem>
                          {sources.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input
                  value={newDsName}
                  onChange={(e) => setNewDsName(e.target.value)}
                  placeholder="Add a data source..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddDataSource();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={handleAddDataSource}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {isEditing && (
              <div className="space-y-4 pt-2 border-t">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Discovery Status</label>
                  <Select
                    value={discoveryStatus}
                    onValueChange={(val) => setDiscoveryStatus(val as UseCaseStatus)}
                  >
                    <SelectTrigger className="h-9 w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="VALIDATING">Validating</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Data Modeling Status</label>
                  <Select
                    value={dataModelingStatus}
                    onValueChange={(val) => setDataModelingStatus(val as UseCaseStatus)}
                  >
                    <SelectTrigger className="h-9 w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="CONNECTED">Connected</SelectItem>
                      <SelectItem value="VALIDATING">Validating</SelectItem>
                      <SelectItem value="CHANGES_REQUESTED">Changes Requested</SelectItem>
                      <SelectItem value="PLAN_APPROVED">Plan Approved</SelectItem>
                      <SelectItem value="IMPLEMENTATION_IN_PROGRESS">Implementation In Progress</SelectItem>
                      <SelectItem value="IMPLEMENTATION_COMPLETE">Implementation Complete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              {isEditing ? 'Save Changes' : 'Create Use Case'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UseCasesSection() {
  const { data: useCasesData, isLoading } = useUseCases();
  const { data: sourcesData } = useSources();
  const canManage = useFeatureFlag(FeatureFlag.ALLOW_USECASE_MANAGEMENT, false);
  const createMutation = useCreateUseCase();
  const updateMutation = useUpdateUseCase();
  const deleteMutation = useDeleteUseCase();

  const [formOpen, setFormOpen] = useState(false);
  const [editingUseCase, setEditingUseCase] = useState<UseCase | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const useCases = useCasesData?.records || [];
  const sources = sourcesData?.records || [];

  if (isLoading) {
    return null;
  }

  if (useCases.length === 0 && !canManage) {
    return null;
  }

  const handleCreate = (data: { name: string; description: string; data_sources: UseCaseDataSource[]; discovery_status: UseCaseStatus; data_modeling_status: UseCaseStatus }) => {
    createMutation.mutate(data);
  };

  const handleEdit = (data: { name: string; description: string; data_sources: UseCaseDataSource[]; discovery_status: UseCaseStatus; data_modeling_status: UseCaseStatus }) => {
    if (!editingUseCase) return;
    updateMutation.mutate({ id: editingUseCase.id, ...data });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
    setDeleteConfirmId(null);
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Your Use Cases for Ellie</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            For each use case, Ellie will connect to your data sources, validate the data with you, capture business context in the knowledge base, and clean &amp; structure everything to fit in the semantic layer.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => {
              setEditingUseCase(null);
              setFormOpen(true);
            }}
            size="sm"
            className="shrink-0"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Use Case
          </Button>
        )}
      </div>

      {useCases.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <Search className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No use cases yet. Create one to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {useCases.map((uc) => (
            <UseCaseCard
              key={uc.id}
              useCase={uc}
              sources={sources}
              canManage={canManage}
              onEdit={() => {
                setEditingUseCase(uc);
                setFormOpen(true);
              }}
              onDelete={() => setDeleteConfirmId(uc.id)}
            />
          ))}
        </div>
      )}

      <UseCaseFormDialog
        key={editingUseCase?.id || 'new'}
        open={formOpen}
        onOpenChange={setFormOpen}
        initialValues={editingUseCase}
        sources={sources}
        onSubmit={editingUseCase ? handleEdit : handleCreate}
      />

      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Use Case</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this use case? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface KBItem {
  id: string;
  item_type: string;
  name: string;
  body?: string;
  tags: string[];
  status: string;
  proposed_by?: string;
  original_item_id?: string;
  parent_id?: string;
  created_at?: string;
}

function usePendingKBArticles() {
  return useQuery({
    queryKey: ['kbPendingArticles'],
    queryFn: async () => {
      const res = await ApiService.getInstance().request('GET', '/knowledge-base-items/tree') as { records: KBItem[] };
      return res.records.filter((r) => r.status === 'pending' && r.item_type === 'article');
    },
    staleTime: 15 * 1000,
  });
}

function KBReviewDialog({
  open,
  onOpenChange,
  articleId,
  onAction,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  articleId: string;
  onAction: () => void;
}) {
  const navigate = useNavigate();
  const { data: article, isLoading } = useKBArticle(open ? articleId : null);
  const queryClient = useQueryClient();

  const approveMutation = useMutation({
    mutationFn: () => ApiService.getInstance().request('POST', `/knowledge-base-items/${articleId}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kbPendingArticles'] });
      onAction();
      onOpenChange(false);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => ApiService.getInstance().request('POST', `/knowledge-base-items/${articleId}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kbPendingArticles'] });
      onAction();
      onOpenChange(false);
    },
  });

  const isActing = approveMutation.isPending || rejectMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{article?.name || 'Knowledge Base Article'}</DialogTitle>
          {article?.created_at && (
            <DialogDescription>
              Proposed {new Date(article.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogBody className="flex-1 min-h-0 overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : article?.body ? (
            <div className="space-y-3">
              {article.tags && article.tags.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  {article.tags.map((tag, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <Markdown remarkPlugins={[remarkGfm]} components={feedbackMarkdownComponents}>{article.body}</Markdown>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No content.</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              navigate(`/knowledge-base/article/${articleId}?edit=true`);
            }}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => rejectMutation.mutate()} disabled={isActing}>
            <XCircle className="h-4 w-4 mr-1" />
            Reject
          </Button>
          <Button onClick={() => approveMutation.mutate()} disabled={isActing}>
            <Check className="h-4 w-4 mr-1" />
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PendingKBArticles() {
  const { data: pendingArticles, isLoading } = usePendingKBArticles();
  const [reviewArticleId, setReviewArticleId] = useState<string | null>(null);

  if (isLoading) {
    return null;
  }

  const hasPending = pendingArticles && pendingArticles.length > 0;

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Pending Knowledge Base Articles</CardTitle>
          </div>
          <CardDescription>
            {hasPending
              ? `${pendingArticles.length} article${pendingArticles.length !== 1 ? 's' : ''} awaiting review. Ellie can't reference these articles until they're approved.`
              : 'No articles pending review. As Ellie learns about your business, new articles will appear here for your approval.'}
          </CardDescription>
        </CardHeader>
        {hasPending && (
          <CardContent>
            <div className="space-y-1">
              {pendingArticles.map((article) => (
                <button
                  key={article.id}
                  className="w-full text-left rounded-md px-3 py-2 hover:bg-muted/50 transition-colors"
                  onClick={() => setReviewArticleId(article.id)}
                >
                  <p className="text-sm font-medium truncate">{article.name}</p>
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap mt-1">
                      {article.tags.slice(0, 3).map((tag, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px] py-0 px-1.5">{tag}</Badge>
                      ))}
                      {article.tags.length > 3 && (
                        <span className="text-[10px] text-muted-foreground">+{article.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {reviewArticleId && (
        <KBReviewDialog
          open={!!reviewArticleId}
          onOpenChange={(open) => { if (!open) setReviewArticleId(null); }}
          articleId={reviewArticleId}
          onAction={() => setReviewArticleId(null)}
        />
      )}
    </>
  );
}

export default function UseCasesPage() {
  return (
    <div className="flex flex-col h-full p-8 relative">
      <div className="md:hidden absolute top-3 left-3">
        <SidebarTrigger className="h-5 w-5" />
      </div>
      <div className="flex gap-6">
        <div className="flex-1 min-w-0">
          <UseCasesSection />
        </div>
        <div className="w-80 shrink-0 hidden lg:block">
          <PendingKBArticles />
        </div>
      </div>
    </div>
  );
}
